import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Autocomplete, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { convertPurchaseRequestToOrder, type PurchaseRequestListItem } from '../../api/achizitiiCereri'
import { listSuppliers } from '../../api/furnizori'
import { listWarehouses } from '../../api/depozite'
import { errorMessage } from '../../api/client'

const schema = z.object({
  supplierId: z.string().min(1, 'Selectați un furnizor'),
  deliveryWarehouseId: z.string().min(1, 'Selectați un depozit'),
  requestedDeliveryDate: z.string().min(1, 'Câmp obligatoriu'),
  paymentTerms: z.string().optional(),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = { supplierId: '', deliveryWarehouseId: '', requestedDeliveryDate: '', paymentTerms: '' }

export default function ConvertRequestToOrderDialog({ request, onClose, onConverted }: {
  request: PurchaseRequestListItem | null; onClose: () => void; onConverted: (orderId: string) => void
}) {
  const open = request !== null
  const qc = useQueryClient()
  const { data: suppliers } = useQuery({ queryKey: ['achizitii', 'furnizori', 'toti'], queryFn: () => listSuppliers(), enabled: open })
  const { data: warehouses } = useQuery({ queryKey: ['stocuri', 'depozite'], queryFn: listWarehouses, enabled: open })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })

  useEffect(() => { if (open) reset(EMPTY) }, [open, reset])

  const mutation = useMutation({
    mutationFn: (values: FormValues) => convertPurchaseRequestToOrder(request!.id, {
      supplierId: values.supplierId, deliveryWarehouseId: values.deliveryWarehouseId,
      requestedDeliveryDate: values.requestedDeliveryDate, paymentTerms: values.paymentTerms || null,
    }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['achizitii', 'cereri'] })
      void qc.invalidateQueries({ queryKey: ['achizitii', 'comenzi'] })
      onConverted(r.id)
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>Conversie cerere {request?.number} în comandă furnizor</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Controller name="supplierId" control={control} render={({ field }) => (
              <Autocomplete
                options={suppliers?.map((s) => s.id) ?? []}
                getOptionLabel={(id) => suppliers?.find((s) => s.id === id)?.name ?? id}
                value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                renderInput={(params) => <TextField {...params} label="Furnizor" error={!!errors.supplierId} helperText={errors.supplierId?.message} />}
              />
            )} />
            <Controller name="deliveryWarehouseId" control={control} render={({ field }) => (
              <Autocomplete
                options={warehouses?.map((w) => w.id) ?? []}
                getOptionLabel={(id) => warehouses?.find((w) => w.id === id)?.name ?? id}
                value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                renderInput={(params) => <TextField {...params} label="Depozit de livrare" error={!!errors.deliveryWarehouseId} helperText={errors.deliveryWarehouseId?.message} />}
              />
            )} />
            <Controller name="paymentTerms" control={control} render={({ field }) => (
              <TextField {...field} label="Termeni de plată (opțional)" fullWidth />
            )} />
            <Controller name="requestedDeliveryDate" control={control} render={({ field }) => (
              <TextField {...field} type="date" label="Dată livrare solicitată" fullWidth slotProps={{ inputLabel: { shrink: true } }}
                error={!!errors.requestedDeliveryDate} helperText={errors.requestedDeliveryDate?.message} />
            )} />
            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Convertește
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
